/** @typedef {'plan.update' | 'plan.update.promo' | 'alert.triggered' | 'robot.commands.update' | 'notification' | 'margin.indicators.update'} SseMessageType */

/** @typedef {'positive' | 'negative'} NotificationVariant */

/**
 * @typedef {object} TriggeredAlert
 * @property {string} stock
 * @property {string} futures
 * @property {number | null} expPercent
 * @property {number | null} divPercent
 * @property {number | null} stockPrice
 * @property {number | null} futuresPrice
 * @property {number | null} spread
 * @property {string | null} broker
 */

/**
 * @typedef {object} PlanUpdatePayload
 * @property {string} timestamp
 * @property {Array<object>} vectorPlan
 * @property {{ keyrate: number, fairSpreads: Array<object> }} metadata
 */

/**
 * @typedef {object} AlertTriggeredPayload
 * @property {string} timestamp
 * @property {TriggeredAlert[]} triggeredAlerts
 */

/**
 * @typedef {object} RobotCommandsUpdatePayload
 * @property {string} timestamp
 * @property {Array<object>} commands
 */

/**
 * @typedef {object} NotificationPayload
 * @property {string} timestamp
 * @property {NotificationVariant} variant
 * @property {unknown} payload
 */

/**
 * @typedef {object} PlanUpdatePromoPayload
 * @property {string} timestamp
 * @property {Array<object>} vectorPlan
 */

/**
 * @typedef {object} MarginIndicatorsData
 * @property {string} displayName
 * @property {string} number
 * @property {number} currentValueRub
 * @property {number} collateral
 * @property {number} collateralAvailable
 * @property {number} borrowedFunds
 * @property {number} varMargin
 */

/**
 * @typedef {object} MarginIndicatorsUpdatePayload
 * @property {string} timestamp
 * @property {MarginIndicatorsData} data
 */

/**
 * @typedef {object} SsePlanUpdateMessage
 * @property {'plan.update'} type
 * @property {PlanUpdatePayload} data
 */

/**
 * @typedef {object} SseAlertTriggeredMessage
 * @property {'alert.triggered'} type
 * @property {AlertTriggeredPayload} data
 */

/**
 * @typedef {object} SseRobotCommandsUpdateMessage
 * @property {'robot.commands.update'} type
 * @property {RobotCommandsUpdatePayload} data
 */

/**
 * @typedef {object} SseNotificationMessage
 * @property {'notification'} type
 * @property {NotificationPayload} data
 */

/**
 * @typedef {object} SsePlanUpdatePromoMessage
 * @property {'plan.update.promo'} type
 * @property {PlanUpdatePromoPayload} data
 */

/**
 * @typedef {object} SseMarginIndicatorsUpdateMessage
 * @property {'margin.indicators.update'} type
 * @property {MarginIndicatorsUpdatePayload} data
 */

/** @typedef {SsePlanUpdateMessage | SsePlanUpdatePromoMessage | SseAlertTriggeredMessage | SseRobotCommandsUpdateMessage | SseNotificationMessage | SseMarginIndicatorsUpdateMessage} SseMessage */

export const SseMessageType = {
  PlanUpdate: 'plan.update',
  PlanUpdatePromo: 'plan.update.promo',
  AlertTriggered: 'alert.triggered',
  RobotCommandsUpdate: 'robot.commands.update',
  Notification: 'notification',
  MarginIndicatorsUpdate: 'margin.indicators.update',
};

const SSE_MESSAGE_TYPES = new Set(Object.values(SseMessageType));

/**
 * @param {unknown} value
 * @returns {value is SseMessage}
 */
export function isSseMessage(value) {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    SSE_MESSAGE_TYPES.has(/** @type {{ type: string }} */ (value).type)
  );
}

/**
 * @param {string} raw
 * @returns {SseMessage | null}
 */
export function parseSseMessage(raw) {
  try {
    const parsed = JSON.parse(raw);
    return isSseMessage(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {SseMessage} message
 * @returns {message is SsePlanUpdateMessage}
 */
export function isPlanUpdateMessage(message) {
  return message.type === SseMessageType.PlanUpdate;
}

/**
 * @param {SseMessage} message
 * @returns {message is SseAlertTriggeredMessage}
 */
export function isAlertTriggeredMessage(message) {
  return message.type === SseMessageType.AlertTriggered;
}

/**
 * @param {SseMessage} message
 * @returns {message is SseRobotCommandsUpdateMessage}
 */
export function isRobotCommandsUpdateMessage(message) {
  return message.type === SseMessageType.RobotCommandsUpdate;
}

/**
 * @param {SseMessage} message
 * @returns {message is SsePlanUpdatePromoMessage}
 */
export function isPlanUpdatePromoMessage(message) {
  return message.type === SseMessageType.PlanUpdatePromo;
}

/**
 * @param {SseMessage} message
 * @returns {message is SseNotificationMessage}
 */
export function isNotificationMessage(message) {
  return message.type === SseMessageType.Notification;
}

/**
 * @param {SseMessage} message
 * @returns {message is SseMarginIndicatorsUpdateMessage}
 */
export function isMarginIndicatorsUpdateMessage(message) {
  return message.type === SseMessageType.MarginIndicatorsUpdate;
}

/**
 * @param {unknown} payload
 * @returns {string}
 */
export function formatNotificationMessage(payload) {
  if (typeof payload === 'string') {
    return payload;
  }

  if (payload && typeof payload === 'object' && typeof payload.message === 'string') {
    return payload.message;
  }

  try {
    return JSON.stringify(payload);
  } catch {
    return 'Уведомление';
  }
}
